import { Box, Button, SizableText, SmartLink, XStack, YStack, colors } from '@my/ui'
import { GroupPost, User } from 'app/types'
import { MessageCircle } from '@tamagui/lucide-icons'
import {
  PostImage,
  PostReactions,
  PostText,
  PostUser,
  GroupUser,
} from '../../components'
import { GroupPostOptions, GroupPostReactionPopupContainer } from '../../containers'
import { useLink } from 'solito/link'

export interface GroupPostCardProps {
  user: Pick<User, 'id' | 'fullName' | 'avatar'> | null
  post: GroupPost
  displayAs?: 'feedItem' | 'post'
  canInteract: boolean
  canExpandContent?: boolean
  onAfterDelete: () => void
}

export const GroupPostCard = ({
  displayAs = 'post',
  user,
  post,
  canInteract,
  canExpandContent,
  onAfterDelete,
}: GroupPostCardProps) => {
  const activeReaction = post.reactions.find((reaction) => reaction.userId === user?.id)
  const totalReactions = post.reactions?.reduce((acc, reaction) => acc + reaction.count, 0) || 0

  const { onPress } = useLink({
    href: post.href,
  })

  return (
    <Box ai="stretch" fd="column" gap="$3.5">
      <XStack flex={1} jc="space-between" ai="flex-start">
        {displayAs === 'post' && (
          <PostUser
            creator={post.creator}
            group={post.group}
            createdAt={post.createdAt}
            editedAt={post.editedAt}
          />
        )}
        {displayAs === 'feedItem' && (
          <GroupUser
            creator={post.creator}
            group={post.group}
            createdAt={post.createdAt}
            editedAt={post.editedAt}
          />
        )}
        <XStack gap="$2">
          <GroupPostOptions post={post} onAfterDelete={onAfterDelete} />
        </XStack>
      </XStack>

      {/* Fake button so that we don't nest links */}
      <YStack
        role="button"
        cursor="pointer"
        gap="$3.5"
        onPress={onPress}
        hoverStyle={{
          opacity: 0.98,
        }}
        focusStyle={{
          opacity: 0.95,
        }}
        pressStyle={{
          opacity: 0.85,
        }}
      >
        <PostText text={post.text} expandable={canExpandContent} />
        <PostImage image={post.image} br="$3" />
      </YStack>

      <XStack flex={1} jc="space-between" ai="center">
        <PostReactions reactions={post.reactions || []} />
        <XStack ml="auto" gap="$2">
          {canInteract && (
            <GroupPostReactionPopupContainer
              postId={post.id}
              user={user}
              activeReaction={activeReaction}
              totalReactions={totalReactions}
            />
          )}
          <SmartLink
            href={post.href}
            Component={Button}
            plain
            size="$2"
            icon={<MessageCircle size={24} color={colors.gray11} />}
          >
            <SizableText color="$gray11" size="$1" fow="500">
              {post.totalComments}
            </SizableText>
          </SmartLink>
        </XStack>
      </XStack>
    </Box>
  )
}
